import { useState, useRef, useEffect, type MouseEvent } from 'react';
import {
  autoUpdate,
  flip,
  FloatingPortal,
  offset,
  shift,
  useFloating,
  type Placement,
} from '@floating-ui/react';
import { Heart, Check, Plus } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useFavoriteActions } from '../../hooks/useFavoriteActions';
import { useSettingsStore } from '../../stores/useSettingsStore';
import { useFavorites } from '../../hooks/useFavorites';
import { useTranslation } from '../../i18n';

interface FavoriteGroupPickerProps {
  productId: string;
  onSimpleToggle: (e: MouseEvent) => void;
  size?: 'sm' | 'md';
  align?: 'left' | 'right';
  direction?: 'up' | 'down';
}

export default function FavoriteGroupPicker({
  productId,
  onSimpleToggle,
  size = 'sm',
  align = 'left',
  direction = 'up',
}: FavoriteGroupPickerProps) {
  const { t } = useTranslation();
  const { isLoggedIn } = useAuth();
  const {
    isFavorite,
    toggleFavorite,
    favoriteGroups,
    addFavoriteToGroup,
    removeFavoriteFromGroup,
    createFavoriteGroup,
  } = useFavoriteActions(isLoggedIn);
  const { favoriteGroupsEnabled } = useSettingsStore();
  const { data: allFavorites = [] } = useFavorites();

  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const ref = useRef<HTMLDivElement>(null);

  const placement: Placement = `${direction === 'down' ? 'bottom' : 'top'}-${
    align === 'right' ? 'end' : 'start'
  }`;

  // floating-ui's `refs.setReference` / `refs.setFloating` are callback-ref
  // setters rather than React refs, but the React 19 lint rule flags any
  // `refs.*` access; the callsites have per-line disables.
  const {
    refs: floatingRefs,
    floatingStyles,
  } = useFloating({
    open,
    onOpenChange: setOpen,
    placement,
    middleware: [offset(4), flip(), shift({ padding: 8 })],
    whileElementsMounted: autoUpdate,
  });

  const favorited = isFavorite(productId);
  const iconSize = size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const numericProductId = Number(productId);
  const favorite = allFavorites.find((f) => Number(f.product_id) === numericProductId);

  useEffect(() => {
    if (!open) return;
    const handler = (e: globalThis.MouseEvent) => {
      const target = e.target as Node;
      const clickedTrigger = ref.current?.contains(target);
      const clickedMenu = floatingRefs.floating.current?.contains(target);
      if (!clickedTrigger && !clickedMenu) {
        setOpen(false);
        setCreating(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open, floatingRefs]);

  if (!favoriteGroupsEnabled) {
    return (
      <button
        type="button"
        title={favorited ? t.actions.removeFromFavorites : t.actions.addToFavorites}
        className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          onSimpleToggle(e);
        }}
      >
        <Heart
          className={`${iconSize} transition-colors ${
            favorited ? 'fill-red-500 text-red-500' : 'text-gray-400 dark:text-gray-500'
          }`}
        />
      </button>
    );
  }

  const handleHeartClick = (e: MouseEvent) => {
    e.stopPropagation();
    setOpen(!open);
  };

  const handleGroupSelect = async (groupId: number, isInGroup: boolean) => {
    if (!favorite) {
      // Product not yet favorited: toggle returns { favorite_id } from the API
      try {
        const result = await toggleFavorite(productId) as { favorite_id?: number } | undefined;
        if (result?.favorite_id) {
          addFavoriteToGroup(result.favorite_id, groupId);
        }
      } catch {
        // toggle failed
      }
      return;
    }
    if (isInGroup) {
      removeFavoriteFromGroup(favorite.id, groupId);
    } else {
      addFavoriteToGroup(favorite.id, groupId);
    }
  };

  const handleCreateGroup = () => {
    const trimmed = newGroupName.trim();
    if (trimmed) {
      createFavoriteGroup(trimmed);
      setNewGroupName('');
      setCreating(false);
    }
  };

  const buttonLabel = favorited ? t.actions.favorited : t.actions.favorite;

  return (
    <div className="relative" ref={ref}>
      {size === 'md' ? (
        <button
          ref={floatingRefs.setReference}
          type="button"
          title={favorited ? t.actions.manageFavorites : t.actions.addToFavorites}
          className={`flex flex-col items-center gap-1 rounded-lg px-3 py-2.5 text-xs font-medium transition-all duration-200 cursor-pointer w-full ${
            favorited
              ? 'bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400'
              : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700 dark:hover:text-gray-200'
          }`}
          onClick={handleHeartClick}
        >
          <Heart
            className={`${iconSize} transition-colors ${
              favorited ? 'fill-red-500 text-red-500' : ''
            }`}
          />
          <span className="leading-none">{buttonLabel}</span>
        </button>
      ) : (
        <button
          // eslint-disable-next-line react-hooks/refs
          ref={floatingRefs.setReference}
          type="button"
          title={favorited ? t.actions.manageFavorites : t.actions.addToFavorites}
          className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors cursor-pointer"
          onClick={handleHeartClick}
        >
          <Heart
            className={`${iconSize} transition-colors ${
              favorited ? 'fill-red-500 text-red-500' : 'text-gray-400 dark:text-gray-500'
            }`}
          />
        </button>
      )}

      {open && (
        <FloatingPortal>
          <div
            // eslint-disable-next-line react-hooks/refs
            ref={floatingRefs.setFloating}
            style={floatingStyles}
            className="z-[60] w-52 rounded-xl bg-white dark:bg-gray-800 shadow-lg border border-gray-200 dark:border-gray-700 py-1"
          >
          <button
            type="button"
            className={`w-full text-left px-3 py-1.5 text-sm font-medium transition-colors cursor-pointer ${
              favorited
                ? 'text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30'
                : 'text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-50 dark:hover:bg-gray-700'
            }`}
            onClick={(e) => {
              e.stopPropagation();
              toggleFavorite(productId);
              if (!favorited) setOpen(false);
            }}
          >
            {favorited ? t.actions.removeFromFavorites : t.actions.addToFavoritesNoGroup}
          </button>
          <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
          <p className="text-[10px] font-semibold text-gray-400 dark:text-gray-500 uppercase tracking-wider px-3 py-1">
            Groups
          </p>
          {favoriteGroups.map((group) => {
            const isInGroup = (group.favorites ?? []).some((f) => f.product_id === numericProductId);
            return (
              <button
                key={group.id}
                type="button"
                className={`w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 transition-colors cursor-pointer ${
                  isInGroup
                    ? 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleGroupSelect(Number(group.id), isInGroup);
                }}
              >
                {isInGroup ? (
                  <Check className="w-3.5 h-3.5 shrink-0 text-red-500" />
                ) : (
                  <span className="w-3.5 shrink-0" />
                )}
                <span className="truncate flex-1">{group.name}</span>
                <span className="text-xs text-gray-400 dark:text-gray-500">{(group.favorites ?? []).length}</span>
              </button>
            );
          })}

          {favoriteGroups.length < 5 && (
            <>
              <div className="border-t border-gray-200 dark:border-gray-700 my-1" />
              {creating ? (
                <form
                  className="px-3 py-1.5 flex gap-1"
                  onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleCreateGroup();
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="text"
                    value={newGroupName}
                    onChange={(e) => setNewGroupName(e.target.value)}
                    placeholder="Group name..."
                    className="flex-1 min-w-0 text-sm px-2 py-1 rounded border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-vivatis-500"
                    autoFocus
                  />
                  <button
                    type="submit"
                    className="shrink-0 px-2 py-1 text-xs font-medium rounded bg-vivatis-600 text-white hover:bg-vivatis-700"
                  >
                    Add
                  </button>
                </form>
              ) : (
                <button
                  type="button"
                  className="w-full text-left px-3 py-1.5 text-sm flex items-center gap-2 text-vivatis-600 dark:text-vivatis-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    setCreating(true);
                  }}
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>New Group</span>
                </button>
              )}
            </>
          )}
          </div>
        </FloatingPortal>
      )}
    </div>
  );
}
